import { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import L from "leaflet";

// Fix broken default marker icons in webpack builds
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

interface LocationForm {
  name: string;
  latitude: string;
  longitude: string;
  radius: number;
}

interface Props {
  show: boolean;
  onHide: () => void;
  onSave: (data: any) => Promise<void>;
  editData?: any;
}

interface Suggestion {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
}

export default function AddLocation({ show, onHide, onSave, editData }: Props) {
  const [form, setForm] = useState<LocationForm>({
    name: "",
    latitude: "",
    longitude: "",
    radius: 20,
  });
  const [address, setAddress] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof LocationForm, string>>>({});

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  const debounceRef = useRef<any>(null);

  // Initialise / destroy Leaflet map imperatively when modal opens/closes
  useEffect(() => {
    if (!show) {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
        circleRef.current = null;
      }
      return;
    }

    // Wait one tick for the modal DOM to mount
    const t = setTimeout(() => {
      if (!mapContainerRef.current || mapRef.current) return;

      const initLat = editData?.location?.coordinates?.[1] ?? 20.5937;
      const initLng = editData?.location?.coordinates?.[0] ?? 78.9629;
      const initZoom = editData ? 15 : 5;

      const map = L.map(mapContainerRef.current, { zoomControl: true }).setView(
        [initLat, initLng],
        initZoom
      );

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(map);

      if (editData) {
        const radius = editData.radius ?? 20;
        markerRef.current = L.marker([initLat, initLng]).addTo(map);
        circleRef.current = L.circle([initLat, initLng], {
          radius,
          color: "#0d6efd",
          fillColor: "#0d6efd",
          fillOpacity: 0.15,
        }).addTo(map);
      }

      map.on("click", (e: L.LeafletMouseEvent) => {
        const { lat, lng } = e.latlng;
        setForm((f) => ({
          ...f,
          latitude: lat.toFixed(7),
          longitude: lng.toFixed(7),
        }));
      });

      mapRef.current = map;
    }, 100);

    return () => clearTimeout(t);
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update marker + circle whenever lat/lng or radius changes
  useEffect(() => {
    if (!mapRef.current) return;
    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    if (isNaN(lat) || isNaN(lng)) return;

    const pos: L.LatLngExpression = [lat, lng];
    const radius = Number(form.radius) || 20;

    if (markerRef.current) {
      markerRef.current.setLatLng(pos);
    } else {
      markerRef.current = L.marker(pos).addTo(mapRef.current);
    }

    if (circleRef.current) {
      circleRef.current.setLatLng(pos).setRadius(radius);
    } else {
      circleRef.current = L.circle(pos, {
        radius,
        color: "#0d6efd",
        fillColor: "#0d6efd",
        fillOpacity: 0.15,
      }).addTo(mapRef.current);
    }

    mapRef.current.flyTo(pos, Math.max(mapRef.current.getZoom(), 14), {
      animate: true,
      duration: 0.8,
    });
  }, [form.latitude, form.longitude, form.radius]);

  // Populate form when editing
  useEffect(() => {
    if (!show) return;
    if (editData) {
      setForm({
        name: editData.name ?? "",
        latitude: String(editData.location?.coordinates?.[1] ?? ""),
        longitude: String(editData.location?.coordinates?.[0] ?? ""),
        radius: editData.radius ?? 20,
      });
      setAddress("");
    } else {
      setForm({ name: "", latitude: "", longitude: "", radius: 20 });
      setAddress("");
    }
    setSuggestions([]);
    setErrors({});
  }, [editData, show]);

  // Debounced Nominatim address search
  const handleAddressChange = (val: string) => {
    setAddress(val);
    clearTimeout(debounceRef.current);
    if (val.length < 3) { setSuggestions([]); return; }
    setLoadingSuggestions(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(val)}&format=json&limit=6`,
          { headers: { "Accept-Language": "en" } }
        );
        setSuggestions(await res.json());
      } catch {
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 500);
  };

  const pickSuggestion = (s: Suggestion) => {
    setAddress(s.display_name);
    setForm((f) => ({
      ...f,
      latitude: parseFloat(s.lat).toFixed(7),
      longitude: parseFloat(s.lon).toFixed(7),
    }));
    setSuggestions([]);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          latitude: pos.coords.latitude.toFixed(7),
          longitude: pos.coords.longitude.toFixed(7),
        }));
        setLocating(false);
      },
      (err) => {
        alert(`Could not get location: ${err.message}`);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const validate = () => {
    const e: Partial<Record<keyof LocationForm, string>> = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.latitude) e.latitude = "Required";
    if (!form.longitude) e.longitude = "Required";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      await onSave({
        name: form.name.trim(),
        location: {
          type: "Point",
          coordinates: [parseFloat(form.longitude), parseFloat(form.latitude)],
        },
        radius: Number(form.radius),
      });
      onHide();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold fs-6">
          {editData ? "Edit Location" : "Add New Location"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        {/* Map */}
        <div style={{ position: "relative", height: 320 }}>
          <div ref={mapContainerRef} style={{ height: "100%", width: "100%" }} />
          <div
            style={{
              position: "absolute",
              bottom: 8,
              left: "50%",
              transform: "translateX(-50%)",
              background: "rgba(0,0,0,0.55)",
              color: "#fff",
              fontSize: 12,
              padding: "3px 12px",
              borderRadius: 20,
              zIndex: 1000,
              pointerEvents: "none",
              whiteSpace: "nowrap",
            }}
          >
            Click on map to place pin
          </div>
          <button
            onClick={useCurrentLocation}
            disabled={locating}
            title="Use my current location"
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 1000,
              background: "#fff",
              border: "2px solid rgba(0,0,0,0.2)",
              borderRadius: 6,
              padding: "5px 10px",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 6,
              boxShadow: "0 1px 5px rgba(0,0,0,0.2)",
            }}
          >
            {locating ? (
              <>
                <Spinner size="sm" animation="border" />
                Locating…
              </>
            ) : (
              <>
                <i className="bi bi-bullseye text-primary" />
                My Location
              </>
            )}
          </button>
        </div>

        {/* Form fields */}
        <div className="p-3">
          {/* Address search */}
          <Form.Group className="mb-3" style={{ position: "relative" }}>
            <Form.Label className="small fw-semibold">Search Address</Form.Label>
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-search" />
              </span>
              <Form.Control
                placeholder="Type an address to search…"
                value={address}
                onChange={(e) => handleAddressChange(e.target.value)}
                autoComplete="off"
              />
              {loadingSuggestions && (
                <span className="input-group-text">
                  <Spinner size="sm" animation="border" />
                </span>
              )}
            </div>

            {suggestions.length > 0 && (
              <div
                className="border rounded shadow-sm bg-white"
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 2000,
                  maxHeight: 220,
                  overflowY: "auto",
                }}
              >
                {suggestions.map((s) => (
                  <div
                    key={s.place_id}
                    className="px-3 py-2 small"
                    style={{ cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
                    onMouseDown={() => pickSuggestion(s)}
                  >
                    <i className="bi bi-geo-alt text-primary me-2" />
                    {s.display_name}
                  </div>
                ))}
              </div>
            )}
          </Form.Group>

          <div className="row g-2">
            {/* Name */}
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="small fw-semibold">Location Name *</Form.Label>
                <Form.Control
                  placeholder="e.g. Head Office, Site A"
                  value={form.name}
                  onChange={(e) => {
                    setForm({ ...form, name: e.target.value });
                    setErrors({ ...errors, name: undefined });
                  }}
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
              </Form.Group>
            </div>

            {/* Radius */}
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="small fw-semibold">
                  Radius:{" "}
                  <span className="text-primary fw-bold">{form.radius} m</span>
                </Form.Label>
                <Form.Range
                  min={5}
                  max={500}
                  step={5}
                  value={form.radius}
                  onChange={(e) =>
                    setForm({ ...form, radius: Number(e.target.value) })
                  }
                />
              </Form.Group>
            </div>

            {/* Lat */}
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="small fw-semibold">Latitude</Form.Label>
                <Form.Control
                  placeholder="Auto-filled from map"
                  value={form.latitude}
                  onChange={(e) => {
                    setForm({ ...form, latitude: e.target.value });
                    setErrors({ ...errors, latitude: undefined });
                  }}
                  isInvalid={!!errors.latitude}
                />
              </Form.Group>
            </div>

            {/* Lng */}
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="small fw-semibold">Longitude</Form.Label>
                <Form.Control
                  placeholder="Auto-filled from map"
                  value={form.longitude}
                  onChange={(e) => {
                    setForm({ ...form, longitude: e.target.value });
                    setErrors({ ...errors, longitude: undefined });
                  }}
                  isInvalid={!!errors.longitude}
                />
              </Form.Group>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={saving}>
          {saving ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              Saving…
            </>
          ) : editData ? (
            "Update Location"
          ) : (
            "Save Location"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
